/* Orion cart enhancement layer (AFC port, Horizon chassis).
   Keeps Horizon's cart-drawer-component for dialog/a11y/refresh mechanics and
   layers on: CART/SAVED tabs (localStorage), free-shipping progress bar,
   complement upsell strip, per-line save-for-later, orionOpenCart() global.
   Config comes from #orion-cart-config (Orion cart settings section). */
(() => {
  const CFG_ID = 'orion-cart-config';
  const LS_KEY = 'orion-saved';
  const CART_UPDATE = 'cart:update';

  const state = {
    cfg: { threshold: 99, upsells: [] },
    tab: 'cart',
    products: {},
    cart: null,
    strings: {},
  };

  function readConfig() {
    const el = document.getElementById(CFG_ID);
    if (!el) return;
    try {
      const parsed = JSON.parse(el.textContent);
      state.cfg = Object.assign(state.cfg, parsed);
      state.strings = parsed.strings || {};
    } catch (e) { /* defaults hold */ }
  }

  function t(key, fallback, vars) {
    let s = state.strings[key] || fallback;
    if (s.indexOf('&') !== -1) {
      const ta = document.createElement('textarea');
      ta.innerHTML = s;
      s = ta.value;
    }
    if (vars) s = s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
    return s;
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ---------- saved list (localStorage) ---------- */
  function savedRead() {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); }
    catch (e) { return {}; }
  }
  function savedWrite(map) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(map)); }
    catch (e) { /* private mode: feature degrades silently */ }
  }

  /* ---------- cart data ---------- */
  async function fetchCart() {
    const r = await fetch('/cart.js');
    state.cart = await r.json();
    return state.cart;
  }

  function drawer() {
    return document.querySelector('cart-drawer-component');
  }

  function sectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId).filter(Boolean);
  }

  function dispatchCartUpdate(cart, sections) {
    /* mirror Horizon's CartAddEvent shape so its components morph natively */
    const ev = new Event(CART_UPDATE, { bubbles: true });
    ev.detail = {
      resource: cart,
      sourceId: 'orion-cart',
      data: {
        source: 'product-form-component',
        itemCount: cart ? cart.item_count : undefined,
        sections: sections || undefined,
      },
    };
    document.dispatchEvent(ev);
  }

  /* Every cart write holds the checkout buttons inert until the cart settles,
     so nobody can check out a cart that is still mid-update. Counted, because
     re-tiering fires several writes back to back. */
  let busyCount = 0;
  function setBusy(on) {
    busyCount = Math.max(0, busyCount + (on ? 1 : -1));
    const d = drawer();
    if (d) d.classList.toggle('orion-cart-busy', busyCount > 0);
  }

  /* Horizon's own quantity stepper writes the cart directly, so a lock that
     only wrapped our writes left the checkout live during exactly the change
     people make most. Watching fetch covers every cart write, whoever issues
     it, without reaching into Horizon's components. */
  (function watchCartWrites() {
    if (window.__orionCartWatch) return;
    window.__orionCartWatch = true;
    const orig = window.fetch;
    window.fetch = function (input, init) {
      const url = String((input && input.url) || input || '');
      const isWrite = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
      if (!isWrite) return orig.apply(this, arguments);
      setBusy(true);
      return orig.apply(this, arguments).finally(() => {
        /* let the morph land before releasing, so the button is not live for
           the frame between response and re-render */
        setTimeout(() => setBusy(false), 250);
      });
    };
  }());

  async function cartMutate(path, payload) {
    setBusy(true);
    const ids = sectionIds();
    if (ids.length) payload.sections = ids.join(',');
    const r = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) { setBusy(false); return null; }
    const resp = await r.json();
    dispatchCartUpdate(await fetchCart(), resp.sections);
    /* a line leaving or changing can move the whole order to another tier */
    if (!reconciling) scheduleSettle();
    setBusy(false);
    return resp;
  }

  /* ---------- injections ---------- */
  /* Horizon's morph can clone our injected nodes when it re-renders the
     section. Everything we add is a singleton, so drop any duplicates before
     we reason about what still needs creating. */
  function dedupe(dialog) {
    ['.orion-cart-tabs', '.orion-ship', '.orion-saved', '.orion-upsells', '.orion-cart-addall', '.orion-subbanner', '.orion-suboffer', '.orion-gifts', '.orion-terms-link']
      .forEach((sel) => {
        const found = dialog.querySelectorAll(sel);
        for (let i = 1; i < found.length; i += 1) found[i].remove();
      });
    /* the morph can clone the strip itself and the cards inside it */
    const strips = dialog.querySelectorAll('[data-orion-upsell-strip]');
    for (let i = 1; i < strips.length; i += 1) strips[i].remove();
    const strip = strips[0];
    if (strip) {
      const seen = new Set();
      [...strip.children].forEach((card) => {
        const id = card.querySelector('[data-orion-upsell-add]')?.dataset.orionUpsellAdd;
        if (!id || seen.has(id)) card.remove();
        else seen.add(id);
      });
      /* the survivor may be stale (e.g. still offering something now in the
         cart), so drop the "unchanged" guard and let renderUpsells rebuild */
      delete strip.dataset.orionSet;
    }
  }

  function ensureInjected() {
    const d = drawer();
    if (!d) return;
    const dialog = d.querySelector('dialog');
    if (!dialog) return;
    dedupe(dialog);
    const header = dialog.querySelector('.cart-drawer__header');
    const content = dialog.querySelector('.cart-drawer__content');
    if (!content) return;

    /* tabs */
    if (!dialog.querySelector('.orion-cart-tabs')) {
      const tabs = document.createElement('div');
      tabs.className = 'orion-cart-tabs';
      tabs.setAttribute('role', 'tablist');
      tabs.innerHTML =
        '<button type="button" role="tab" class="orion-cart-tab is-on" data-orion-tab="cart">'
        + t('tabCart', 'Cart') + ' <span class="orion-cart-count" data-orion-cart-count>0</span></button>'
        + '<button type="button" role="tab" class="orion-cart-tab" data-orion-tab="saved">'
        + t('tabSaved', 'Saved') + ' <span class="orion-cart-count" data-orion-saved-count>0</span></button>';
      (header || content).insertAdjacentElement(header ? 'afterend' : 'beforebegin', tabs);
      tabs.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-orion-tab]');
        if (!btn) return;
        state.tab = btn.dataset.orionTab;
        renderTabs();
      });
    }

    /* ship bar */
    if (!dialog.querySelector('.orion-ship') && state.cfg.threshold > 0) {
      const bar = document.createElement('div');
      bar.className = 'orion-ship';
      bar.innerHTML = '<p class="orion-ship__label" data-orion-ship-label aria-live="polite"></p>'
        + '<div class="orion-ship__track"><div class="orion-ship__fill" data-orion-ship-fill></div></div>';
      dialog.querySelector('.orion-cart-tabs').insertAdjacentElement('afterend', bar);
    }

    /* saved panel */
    if (!dialog.querySelector('.orion-saved')) {
      const panel = document.createElement('div');
      panel.className = 'orion-saved orion-cart-hidden';
      panel.innerHTML = '<p class="orion-saved__hint">' + t('savedHint', 'Kept for later, saved on this device.') + '</p>'
        + '<div data-orion-saved-list></div>'
        + '<p class="orion-saved__empty orion-cart-hidden" data-orion-saved-empty>' + t('savedEmpty', 'Nothing saved yet.') + '</p>';
      content.insertAdjacentElement('beforebegin', panel);
      const addAll = document.createElement('button');
      addAll.type = 'button';
      addAll.className = 'orion-cart-addall orion-cart-hidden';
      addAll.textContent = t('addAll', 'Add all to cart');
      addAll.addEventListener('click', moveAllToCart);
      panel.appendChild(addAll);
      panel.addEventListener('click', (e) => {
        const move = e.target.closest('[data-orion-move]');
        if (move) return moveToCart(move.dataset.orionMove);
        const rm = e.target.closest('[data-orion-unsave]');
        if (rm) {
          const map = savedRead();
          delete map[rm.dataset.orionUnsave];
          savedWrite(map);
          renderSaved();
        }
      });
    }

    /* The band itself is server-rendered (see header-actions.liquid) so the
       morph owns it; we only wire the click handler and fill in the cards. */
    const wrap = dialog.querySelector('[data-orion-upsells]');
    if (wrap && !wrap.dataset.orionBound) {
      wrap.dataset.orionBound = '1';
      renderUpsells();
      wrap.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-orion-upsell-add]');
        if (!btn) return;
        /* optimistic: the card leaves immediately so the tap feels instant.
           If the add fails it comes back on the next refresh. */
        const card = btn.closest('.orion-upsell');

        /* Products with options must be built, not guessed. Adding
           btn.dataset.orionUpsellAdd blind picked the first variant, which on
           the 75-serving bundle silently chose three flavours for the
           customer. orionQuickAdd returns false when there is nothing to
           choose, and only then does the optimistic add apply. */
        const handle = btn.dataset.orionUpsellHandle;
        if (handle && window.orionQuickAdd) {
          btn.disabled = true;
          const opened = await window.orionQuickAdd(handle);
          btn.disabled = false;
          if (opened) return;
        }

        btn.disabled = true;
        if (card) {
          card.classList.add('is-adding');
          card.addEventListener('transitionend', () => card.remove(), { once: true });
          setTimeout(() => card.remove(), 260);
        }
        await cartMutate('/cart/add.js', { items: [{ id: Number(btn.dataset.orionUpsellAdd), quantity: 1 }] });
      });
    }

    /* Recurring-purchase terms: on phones the wallets' disclosure text is
       hidden by CSS and this tiny link opens it in a modal instead. The
       dialog lives on document.body, outside the morph zone, and reads the
       (hidden but present) disclosure at open time so the wording is always
       whatever Shopify rendered. */
    const disclosure = dialog.querySelector('.dynamic-checkout__content small');
    const ctas = dialog.querySelector('.cart__ctas');
    if (disclosure && ctas && !ctas.querySelector('.orion-terms-link')) {
      const link = document.createElement('button');
      link.type = 'button';
      link.className = 'orion-terms-link';
      link.textContent = t('termsLink', 'Recurring purchase terms');
      link.addEventListener('click', () => {
        let terms = document.getElementById('orion-terms-dialog');
        if (!terms) {
          terms = document.createElement('dialog');
          terms.id = 'orion-terms-dialog';
          terms.className = 'orion-terms';
          terms.innerHTML = '<div class="orion-terms__sheet">'
            + '<p class="orion-terms__title">' + t('termsTitle', 'Recurring purchase terms') + '</p>'
            + '<p class="orion-terms__body" data-orion-terms-body></p>'
            + '<button type="button" class="orion-terms__close">' + t('termsClose', 'Close') + '</button>'
            + '</div>';
          terms.querySelector('.orion-terms__close').addEventListener('click', () => terms.close());
          terms.addEventListener('click', (e) => { if (e.target === terms) terms.close(); });
          document.body.appendChild(terms);
        }
        const src = document.querySelector('cart-drawer-component .dynamic-checkout__content small');
        terms.querySelector('[data-orion-terms-body]').innerHTML = src ? src.innerHTML : '';
        terms.showModal();
      });
      ctas.appendChild(link);
    }

    /* At qty 1 the minus becomes the remove. Horizon's stepper both disables
       the minus at the floor and wins any event race for clicks on it (its
       handler clamps back to 1), so the honest approach is our own button:
       the native minus hides and an identically-sized trash button takes its
       slot, wired directly — no delegation fight. The standalone trash
       next to the stepper is hidden by CSS: one control, one row. */
    dialog.querySelectorAll('.cart-items__table-row').forEach((row) => {
      const input = row.querySelector('.quantity-selector input');
      const minus = row.querySelector('.quantity-minus');
      if (!input || !minus) return;
      /* The trash sits OVER the minus, not inside the quantity component:
         Horizon's stepper reasons about its own children positionally, so an
         extra button in there made it mistake the plus for another control
         and remove the line instead of incrementing it. Anchoring to the
         controls wrapper keeps its child list exactly as shipped. */
      const wrap = minus.closest('.quantity-selector-wrapper') || minus.parentElement;
      const isOne = Number(input.value) <= 1;
      wrap.classList.toggle('orion-qty-remove-mode', isOne);
      let mine = wrap.querySelector(':scope > .orion-line-remove');
      if (isOne && !mine) {
        mine = document.createElement('button');
        mine.type = 'button';
        mine.className = 'orion-line-remove';
        mine.setAttribute('aria-label', t('removeLine', 'Remove from cart'));
        mine.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (mine.disabled) return;
          const line = mine.closest('[data-key]');
          if (!line || !line.dataset.key) return;

          /* This button only exists while the line holds one case, and the
             drawer re-renders around it constantly. If the row has since been
             incremented (or already left the cart), removing it would throw
             away a line the customer is actively growing, so confirm against
             the live cart rather than trusting the render we were built in. */
          const live = await fetchCart();
          const still = (live.items || []).find((i) => i.key === line.dataset.key);
          if (!still) return;
          if (still.quantity > 1) {
            mine.remove();
            scheduleInject();
            return;
          }

          mine.disabled = true;
          const resp = await cartMutate('/cart/change.js', { id: line.dataset.key, quantity: 0 });
          if (!resp) mine.disabled = false;
        });
        wrap.insertBefore(mine, wrap.firstChild);
      } else if (!isOne && mine) {
        mine.remove();
      }
    });

    /* per-line save-for-later */
    dialog.querySelectorAll('.cart-items__table-row').forEach((row) => {
      if (row.querySelector('.orion-save-line')) return;
      const remove = row.querySelector('.cart-items__remove, a[href*="quantity=0"], button[aria-label*="emove"]:not(.orion-line-remove)');
      if (!remove) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'orion-save-line';
      btn.textContent = t('saveLine', 'Save for later');
      btn.addEventListener('click', () => saveLine(row));
      remove.insertAdjacentElement('afterend', btn);
    });

    /* freshly injected nodes start at placeholder values ("0", empty bar);
       repaint them from the cart we already have, then refresh in the
       background if we have never fetched one */
    if (state.cart) {
      paint(state.cart);
      renderUpsells();
    } else {
      refreshDynamic();
    }

  }

  function renderTabs() {
    const d = drawer();
    if (!d) return;
    const dialog = d.querySelector('dialog');
    const onSaved = state.tab === 'saved';
    dialog.querySelectorAll('[data-orion-tab]').forEach((b) => {
      const on = b.dataset.orionTab === state.tab;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    const content = dialog.querySelector('.cart-drawer__content');
    const saved = dialog.querySelector('.orion-saved');
    const addAll = dialog.querySelector('.orion-cart-addall');
    const ups = dialog.querySelector('.orion-upsells');
    const ship = dialog.querySelector('.orion-ship');
    if (content) content.classList.toggle('orion-cart-hidden', onSaved);
    if (ups) ups.classList.toggle('orion-cart-hidden', onSaved);
    if (ship) ship.classList.toggle('orion-cart-hidden', onSaved);
    if (saved) saved.classList.toggle('orion-cart-hidden', !onSaved);
    if (addAll) {
      const has = Object.keys(savedRead()).length > 0;
      addAll.classList.toggle('orion-cart-hidden', !onSaved || !has);
    }
    if (onSaved) renderSaved();
  }

  function renderSaved() {
    const d = drawer();
    if (!d) return;
    const list = d.querySelector('[data-orion-saved-list]');
    const empty = d.querySelector('[data-orion-saved-empty]');
    const countEl = d.querySelector('[data-orion-saved-count]');
    const map = savedRead();
    const keys = Object.keys(map);
    if (countEl) countEl.textContent = String(keys.length);
    if (!list) return;
    list.replaceChildren(...keys.map((vid) => {
      const it = map[vid];
      const row = document.createElement('div');
      row.className = 'orion-saved-item';
      const img = document.createElement('img');
      img.src = it.image || '';
      img.alt = '';
      row.appendChild(img);
      const info = document.createElement('div');
      info.className = 'orion-saved-item__info';
      const title = document.createElement('p');
      title.className = 'orion-saved-item__title';
      title.textContent = it.title;
      info.appendChild(title);
      const price = document.createElement('p');
      price.className = 'orion-saved-item__price';
      price.textContent = it.price || '';
      info.appendChild(price);
      row.appendChild(info);
      const move = document.createElement('button');
      move.type = 'button';
      move.className = 'orion-saved-item__move';
      move.dataset.orionMove = vid;
      move.textContent = t('moveToCart', 'Move to cart');
      row.appendChild(move);
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'orion-saved-item__remove';
      rm.dataset.orionUnsave = vid;
      rm.setAttribute('aria-label', t('removeSaved', 'Remove from saved'));
      rm.textContent = '✕';
      row.appendChild(rm);
      return row;
    }));
    if (empty) empty.classList.toggle('orion-cart-hidden', keys.length > 0);
    const addAll = d.querySelector('.orion-cart-addall');
    if (addAll) addAll.classList.toggle('orion-cart-hidden', state.tab !== 'saved' || keys.length === 0);
  }

  async function saveLine(row) {
    const cart = state.cart || await fetchCart();
    /* find the line whose remove link / key matches this row */
    const keyEl = row.querySelector('[data-line-item-key], [data-key]');
    let item = null;
    if (keyEl) {
      const key = keyEl.dataset.lineItemKey || keyEl.dataset.key;
      item = cart.items.find((i) => i.key === key);
    }
    if (!item) {
      /* fallback: match by product url present in the row */
      const link = row.querySelector('a[href*="/products/"]');
      if (link) {
        const path = link.getAttribute('href').split('?')[0];
        item = cart.items.find((i) => i.url.split('?')[0] === path);
      }
    }
    if (!item) return;
    const map = savedRead();
    map[item.variant_id] = {
      title: item.product_title,
      variant: item.variant_title,
      price: money(item.final_price),
      image: item.image,
      url: item.url,
    };
    savedWrite(map);
    await cartMutate('/cart/change.js', { id: item.key, quantity: 0 });
    renderSaved();
  }

  async function moveToCart(vid) {
    const map = savedRead();
    const resp = await cartMutate('/cart/add.js', { items: [{ id: Number(vid), quantity: 1 }] });
    if (resp) {
      delete map[vid];
      savedWrite(map);
      renderSaved();
    }
  }

  async function moveAllToCart() {
    const map = savedRead();
    const ids = Object.keys(map);
    if (!ids.length) return;
    const resp = await cartMutate('/cart/add.js', { items: ids.map((id) => ({ id: Number(id), quantity: 1 })) });
    if (resp) {
      savedWrite({});
      renderSaved();
      state.tab = 'cart';
      renderTabs();
    }
  }

  async function renderUpsells() {
    const d = drawer();
    if (!d) return;
    const strip = d.querySelector('[data-orion-upsell-strip]');
    if (!strip) return;
    const cart = state.cart || await fetchCart();
    const inCart = new Set(cart.items.map((i) => i.handle));
    /* Skip the rebuild when the visible set hasn't changed — replacing
       identical nodes is what made the strip flicker on every update. */
    const wanted = state.cfg.upsells.filter((h) => !inCart.has(h)).join(',');
    if (strip.dataset.orionSet === wanted) return;
    strip.dataset.orionSet = wanted;
    const cards = [];
    for (const handle of state.cfg.upsells) {
      if (inCart.has(handle)) continue;
      if (!state.products[handle]) {
        try {
          const r = await fetch('/products/' + handle + '.js');
          if (!r.ok) continue;
          state.products[handle] = await r.json();
        } catch (e) { continue; }
      }
      const p = state.products[handle];
      const v = p.variants.find((x) => x.available) || p.variants[0];
      if (!v) continue;
      const card = document.createElement('div');
      card.className = 'orion-upsell';
      const img = document.createElement('img');
      img.src = p.featured_image || (p.images && p.images[0]) || '';
      img.alt = '';
      img.loading = 'lazy';
      card.appendChild(img);
      const title = document.createElement('p');
      title.className = 'orion-upsell__title';
      title.textContent = p.title;
      card.appendChild(title);
      const rowEl = document.createElement('div');
      rowEl.className = 'orion-upsell__row';
      const price = document.createElement('span');
      price.className = 'orion-upsell__price';
      price.textContent = p.variants.length > 1 ? t('fromPrice', 'From {p}', { p: money(v.price) }) : money(v.price);
      rowEl.appendChild(price);
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'orion-upsell__add';
      add.dataset.orionUpsellAdd = v.id;
      add.dataset.orionUpsellHandle = handle;
      add.setAttribute('aria-label', t('addUpsell', 'Add {title}', { title: p.title }));
      add.textContent = '+';
      rowEl.appendChild(add);
      card.appendChild(rowEl);
      cards.push(card);
    }
    strip.replaceChildren(...cards);
    const wrap = d.querySelector('.orion-upsells');
    if (wrap) wrap.classList.toggle('orion-cart-hidden', cards.length === 0 || state.tab === 'saved');
  }

  /* ---------- ladder plan reconciliation ----------
     The DPP ladder prices by box size: "Box of 60" is 40% off *because* it is
     sixty packs. Shopify keeps whatever plan was attached when the quantity
     changes in the cart, so dropping a 60-pack line to one case kept the 40%
     and sold fifteen packs at the sixty-pack rate. After every cart change we
     re-attach the plan that matches the quantity the line actually holds, so
     price per pack always equals the advertised tier. */
  const LADDER = /^Box of (\d+)\s*·\s*every (\d+) days$/;

  async function productFor(handle) {
    if (!state.products[handle]) {
      try {
        const r = await fetch('/products/' + handle + '.js');
        if (!r.ok) return null;
        state.products[handle] = await r.json();
      } catch (e) { return null; }
    }
    return state.products[handle];
  }

  function ladderPlans(product) {
    const out = [];
    (product.selling_plan_groups || []).forEach((g) => {
      (g.selling_plans || []).forEach((pl) => {
        const m = LADDER.exec(pl.name);
        if (m) out.push({ id: pl.id, size: Number(m[1]), days: Number(m[2]) });
      });
    });
    return out;
  }

  /* The box is the WHOLE order at a cadence, not one line. A 45-pack built in
     the configurator arrives as three 15-count cases in three flavours, so the
     tier comes from the packs summed across every ladder line sharing that
     cadence, and each of those lines carries that same tier's plan. Judging a
     line on its own quantity would price a three-flavour 45-pack as three
     separate 15-packs. */
  async function ladderLines(cart) {
    const rows = [];
    for (const item of cart.items || []) {
      const alloc = item.selling_plan_allocation;
      if (!alloc || !alloc.selling_plan) continue;
      const m = LADDER.exec(alloc.selling_plan.name);
      if (!m) continue;
      const product = await productFor(item.handle);
      if (!product) continue;
      const plans = ladderPlans(product);
      if (!plans.length) continue;
      rows.push({
        item,
        size: Number(m[1]),
        days: Number(m[2]),
        plans,
        caseSize: Math.min(...plans.map((p) => p.size)),
      });
    }
    return rows;
  }

  /* Groups ladder lines by cadence and returns the plan each group should be
     on, given the packs it actually adds up to. */
  function ladderTargets(rows) {
    const byCadence = {};
    rows.forEach((r) => {
      (byCadence[r.days] = byCadence[r.days] || []).push(r);
    });
    const targets = [];
    Object.keys(byCadence).forEach((days) => {
      const group = byCadence[days];
      const caseSize = group[0].caseSize;
      const tiers = group[0].plans.filter((p) => p.days === Number(days));
      if (!tiers.length) return;
      const packs = group.reduce((n, r) => n + r.item.quantity * r.caseSize, 0);
      const top = tiers.reduce((a, b) => (b.size > a.size ? b : a));
      /* an exact tier if the order lands on one, otherwise the biggest box
         it clears: never a discount the order has not earned */
      const plan = tiers.find((p) => p.size === packs)
        || tiers.filter((p) => p.size <= packs).sort((a, b) => b.size - a.size)[0]
        || tiers.sort((a, b) => a.size - b.size)[0];
      targets.push({ group, plan, packs, top, caseSize });
    });
    return targets;
  }

  /* The quarterly box is three bags sold as one thing. Its plan carries the
     3-month discount per unit, so trimming the cart to a single bag would bill
     one month at the three-month rate. If the box is no longer whole, the plan
     comes off and the bags revert to their honest one-time price. */
  const QUARTERLY = /every 3 months/i;
  const QUARTERLY_BAGS = 3;

  async function reconcileQuarterly(cart) {
    if (!cart || !cart.items) return false;
    const rows = cart.items.filter((i) => {
      const sp = i.selling_plan_allocation && i.selling_plan_allocation.selling_plan;
      return sp && QUARTERLY.test(sp.name);
    });
    if (!rows.length) return false;
    const bags = rows.reduce((n, i) => n + i.quantity, 0);
    if (bags === QUARTERLY_BAGS) return false;

    /* Strip the plan from every remaining bag, not just the first. Dropping a
       plan rewrites that line's key, so re-read between writes; our own writes
       suppress the settle pass, which means this has to finish the job here. */
    let changed = false;
    for (let pass = 0; pass < 8; pass += 1) {
      const live = await fetchCart();
      const stale = (live.items || []).find((i) => {
        const sp = i.selling_plan_allocation && i.selling_plan_allocation.selling_plan;
        return sp && QUARTERLY.test(sp.name);
      });
      if (!stale) break;
      await cartMutate('/cart/change.js', {
        id: stale.key, quantity: stale.quantity, selling_plan: null,
      });
      changed = true;
    }
    return changed;
  }

  /* Runs until the whole cart sits on the right tier. A plan change rewrites
     that line's key, so each pass re-reads the cart and fixes one line; and
     because our own mutations deliberately do not re-trigger the refresh
     cycle, the loop has to converge here rather than across refreshes.
     Returns true if anything changed. */
  let reconciling = false;

  /* Speed matters here: this runs while the customer watches a price. Work out
     every line that needs moving from the cart we already hold, then write them
     back to back without re-reading or re-rendering between each — one refresh
     at the end. Changing a line's plan rewrites only that line's key, so the
     keys captured up front stay valid for the others. */
  async function reconcileLadder(cart) {
    if (reconciling || !cart || !cart.items) return false;
    reconciling = true;
    try {
      if (await reconcileQuarterly(cart)) return true;
      const targets = ladderTargets(await ladderLines(cart));
      const writes = [];
      targets.forEach((t) => {
        t.group.forEach((row) => {
          if (row.size === t.plan.size) return;
          writes.push({ id: row.item.key, quantity: row.item.quantity, selling_plan: t.plan.id });
        });
      });
      if (!writes.length) return false;

      /* paint the destination immediately so the number moves with the tap
         rather than after the round trips */
      previewLadder(writes, targets);

      const ids = sectionIds();
      for (const w of writes) {
        const payload = Object.assign({}, w);
        if (ids.length) payload.sections = ids.join(',');
        /* eslint-disable no-await-in-loop */
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      dispatchCartUpdate(await fetchCart());
      return true;
    } finally {
      reconciling = false;
    }
  }

  /* Optimistic repaint of the line prices we are about to write, so the drawer
     never shows a tier the customer has already left. */
  function previewLadder(writes, targets) {
    const d = drawer();
    if (!d) return;
    const pct = {};
    targets.forEach((t) => { pct[t.plan.id] = t.plan; });
    writes.forEach((w) => {
      const row = d.querySelector('[data-key="' + w.id + '"]');
      if (!row) return;
      row.classList.add('orion-line-repricing');
    });
  }

  /* Our own cart writes skip the refresh cycle to avoid feedback loops, so
     removing or converting a line would otherwise leave the rest of the order
     on a tier it no longer qualifies for. Settle explicitly instead. */
  /* Horizon writes quantities on its own schedule, so settling immediately
     after our write can read a cart that is one request out of date and hand
     back a stale quantity. Wait for the writes to stop, then reconcile once. */
  let settleTimer = null;
  let settlePending = false;
  function scheduleSettle() {
    if (reconciling) return;
    clearTimeout(settleTimer);
    /* hold the checkout lock across the debounce as well: a pending re-tier
       is still an unsettled cart, even while no request is in flight */
    if (!settlePending) { settlePending = true; setBusy(true); }
    settleTimer = setTimeout(async () => {
      try {
        await settleLadder();
      } finally {
        settlePending = false;
        setBusy(false);
      }
    }, 400);
  }

  async function settleLadder() {
    if (reconciling) return;
    const cart = await fetchCart();
    if (await reconcileLadder(cart)) {
      const fresh = await fetchCart();
      paint(fresh);
      const d = drawer();
      const dialog = d && d.querySelector('dialog');
      if (dialog) capLadderSteppers(dialog, fresh);
    }
  }

  /* A ladder line cannot grow past its top tier without the plan name lying
     about the box size, so the plus button stops there. */
  function capLadderSteppers(dialog, cart) {
    if (!cart || !cart.items) return;
    /* the ceiling belongs to the order, not the line: three flavours of a
       60-pack are already at the top, however small each line looks */
    const packsAtCadence = {};
    const topAtCadence = {};
    cart.items.forEach((item) => {
      const alloc = item.selling_plan_allocation;
      if (!alloc || !alloc.selling_plan) return;
      const m = LADDER.exec(alloc.selling_plan.name);
      if (!m) return;
      const product = state.products[item.handle];
      if (!product) return;
      const plans = ladderPlans(product);
      if (!plans.length) return;
      const days = m[2];
      const caseSize = Math.min(...plans.map((p) => p.size));
      packsAtCadence[days] = (packsAtCadence[days] || 0) + item.quantity * caseSize;
      topAtCadence[days] = Math.max(...plans.map((p) => p.size));
    });

    dialog.querySelectorAll('.cart-items__table-row').forEach((row) => {
      const plus = row.querySelector('.quantity-plus');
      const key = row.dataset.key;
      if (!plus || !key) return;
      const item = cart.items.find((i) => i.key === key);
      const alloc = item && item.selling_plan_allocation;
      if (!alloc || !alloc.selling_plan) return;

      /* The quarterly box is a fixed three bags: its quantity is not the
         customer's to tune line by line, so both steppers lock and removal
         stays the way out. The reconcile above is the backstop if a quantity
         changes anyway. */
      if (QUARTERLY.test(alloc.selling_plan.name)) {
        const minus = row.querySelector('.quantity-minus');
        plus.disabled = true;
        plus.title = t('boxFixed', '3-month supply is a fixed 3 bags');
        if (minus) { minus.disabled = true; minus.title = plus.title; }
        return;
      }

      const m = LADDER.exec(alloc.selling_plan.name);
      if (!m) return;
      const days = m[2];
      const atTop = (packsAtCadence[days] || 0) >= (topAtCadence[days] || Infinity);
      plus.disabled = atTop;
      plus.title = atTop ? t('ladderMax', 'Largest box available') : '';
    });
  }

  /* Paint is synchronous and cheap: it repaints whatever is in the DOM right
     now from a cart we already hold. Injection calls it immediately so newly
     created nodes never sit at their placeholder values. */
  function paint(cart) {
    const d = drawer();
    if (!d || !cart) return;
    const countEl = d.querySelector('[data-orion-cart-count]');
    if (countEl) countEl.textContent = String(cart.item_count);
    const label = d.querySelector('[data-orion-ship-label]');
    const fill = d.querySelector('[data-orion-ship-fill]');
    if (label && fill) {
      const threshold = state.cfg.threshold * 100;
      const left = threshold - cart.total_price;
      const bar = d.querySelector('.orion-ship');
      /* every subscription order ships free, so a sub in the cart unlocks the
         bar regardless of subtotal — showing "$X away" next to a line that
         already earns free shipping reads as a broken promise */
      const hasSub = cart.items.some((i) => i.selling_plan_allocation);
      if (threshold <= 0) {
        if (bar) bar.classList.add('orion-cart-hidden');
      } else if (left <= 0 || hasSub) {
        bar.classList.add('is-done');
        label.innerHTML = '<strong>' + t('shipDone', 'Free shipping unlocked') + '</strong>';
        fill.style.width = '100%';
      } else {
        bar.classList.remove('is-done');
        label.textContent = t('shipLeft', '{left} away from free shipping', { left: money(left) });
        fill.style.width = Math.min(100, (cart.total_price / threshold) * 100) + '%';
      }
    }
    renderSaved();
  }

  async function refreshDynamicNow() {
    const d = drawer();
    if (!d) return;
    const cart = await fetchCart();
    /* Fix the plan before painting a price we would have to correct a moment
       later; the mutation triggers its own refresh, so stop this pass here. */
    if (await reconcileLadder(cart)) return;
    paint(cart);
    renderUpsells();
    const dialog = d.querySelector('dialog');
    if (dialog) capLadderSteppers(dialog, cart);
  }

  /* cart changes arrive in bursts (mutation + morph + observer); coalesce them
     into a single pass per frame so the drawer updates once, not three times */
  let refreshQueued = false;
  function refreshDynamic() {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(() => {
      refreshQueued = false;
      refreshDynamicNow();
    });
  }

  /* ---------- wiring ----------
     ensureInjected() only adds missing scaffolding and never touches the
     network; refreshDynamic() updates data and is called explicitly. The
     observer is suspended while we mutate, otherwise our own DOM writes
     re-trigger it and the drawer thrashes on every click. */
  let observer = null;
  let injecting = false;
  let scheduled = false;

  function scheduleInject() {
    if (scheduled || injecting) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      withObserverPaused(ensureInjected);
    });
  }

  function withObserverPaused(fn) {
    injecting = true;
    if (observer) observer.disconnect();
    try {
      fn();
    } finally {
      const d = drawer();
      if (observer && d) observer.observe(d, { childList: true, subtree: true });
      injecting = false;
    }
  }

  function init() {
    readConfig();
    withObserverPaused(ensureInjected);
    refreshDynamic();

    /* Convert a one-time line to its subscription plan in place. The slot is
       server-rendered and morph-owned, so a document-level delegate survives
       every re-render; after the change Liquid swaps the offer for the free
       gifts on its own. */
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-orion-line-sub]');
      if (!btn || btn.disabled) return;
      btn.disabled = true;
      const resp = await cartMutate('/cart/change.js', {
        id: btn.dataset.lineKey,
        quantity: Number(btn.dataset.lineQty),
        selling_plan: Number(btn.dataset.planId),
      });
      /* only on failure: the button survives the morph untouched, so free it */
      if (!resp) btn.disabled = false;
    });

    /* Deep link: /#cart opens the drawer once the page settles. Marketing
       links can land straight in the cart, and device tests can drive the
       drawer open without a tap. */
    if (window.location.hash === '#cart') {
      window.setTimeout(() => window.orionOpenCart(), 400);
    }

    document.addEventListener('orion:cart:added', async (e) => {
      /* open first, then morph: dispatching the update can replace the dialog
         subtree, which would drop a just-set open state */
      window.orionOpenCart();
      dispatchCartUpdate(await fetchCart(), e.detail && e.detail.sections);
    });

    /* Horizon morphs the drawer on cart:update — re-inject on the next frame
       rather than a fixed timeout, so it lands as soon as the DOM settles. */
    document.addEventListener(CART_UPDATE, (e) => {
      scheduleInject();
      if (!e.detail || e.detail.sourceId !== 'orion-cart') {
        refreshDynamic();
        /* a Horizon quantity change can move the order to another tier */
        scheduleSettle();
      }
    });

    const d = drawer();
    if (d) {
      observer = new MutationObserver(() => scheduleInject());
      observer.observe(d, { childList: true, subtree: true });
      d.addEventListener('dialog:open', () => refreshDynamic());
    }
  }

  window.orionOpenCart = function () {
    const d = drawer();
    if (!d) { window.location.href = '/cart'; return; }
    if (typeof d.showDialog === 'function') d.showDialog();
    else {
      const dlg = d.querySelector('dialog');
      if (dlg && !dlg.open) dlg.showModal();
    }
    refreshDynamic();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
